import { usePermissions } from '@/contexts/PermissionsContext';
import type { SidebarItem } from '@/config/layoutConfig';
import { prefetchByPath } from '@/utils/routePrefetch';

export interface AppSidebarProps {
  /** Contrôle de la visibilité (mobile) */
  isOpen: boolean;
  /** Items de navigation */
  items: SidebarItem[];
  /** Action spéciale pour la calculatrice (injectée par le parent) */
  onCalculatorClick?: () => void;
  /** Action spéciale pour le rapport du jour (injectée par le parent) */
  onReportClick?: () => void;
}

/**
 * Sidebar verticale du layout principal.
 * Icônes uniquement, avec tooltips au survol.
 */
export function AppSidebar({ isOpen, items, onCalculatorClick, onReportClick }: AppSidebarProps) {
  const { hasPermission } = usePermissions();

  const resolvedItems = items.map(item => {
    if (item.name === 'Calculatrice' && onCalculatorClick)
      return { ...item, action: onCalculatorClick };
    if (item.name === 'Rapport du jour' && onReportClick) return { ...item, action: onReportClick };
    return item;
  });

  return (
    <aside
      className={`${isOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-16 layout-sidebar shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex-shrink-0`}
    >
      <div className="flex flex-col h-full pt-20 lg:pt-0 w-16">
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto overflow-x-hidden">
            <div className="flex items-center justify-center flex-shrink-0 px-2 mb-6">
              <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">L</span>
              </div>
            </div>
            <nav className="flex-1 px-2 space-y-2">
              {resolvedItems
                .filter(item => hasPermission(item.permissionKey))
                .map(item => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.name}
                      onMouseEnter={() => item.prefetchPath && prefetchByPath(item.prefetchPath)}
                      onClick={item.action || (() => console.log(item.name + ' - À implémenter'))}
                      className="group flex items-center justify-center p-3 w-12 h-12 rounded transition-all duration-200 relative mx-auto layout-sidebar-btn cursor-pointer"
                      title={item.name}
                    >
                      <Icon className="h-6 w-6 flex-shrink-0" />
                      <div className="absolute left-full ml-2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        {item.name}
                      </div>
                    </button>
                  );
                })}
            </nav>
          </div>
        </div>
      </div>
    </aside>
  );
}
